/**
 * Analytics Overview Page - adapted from Next.js
 * Removed 'use client', @/utils/trpc → @/lib/trpc
 */

import { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { KPICard, DateRangePicker, type DateRange } from "@/components/shared";
import { chartColors, defaultChartTheme } from "@/components/charts/theme";
import {
  ChartEmpty,
  ChartLoading,
  ChartError,
  ResponsiveChartContainer,
} from "@/components/charts/ChartStates";
import { trpc } from "@/lib/trpc";

export default function AnalyticsOverviewPage() {
  const [dateRange, setDateRange] = useState<DateRange>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date(),
  });
  const dateParams = useMemo(
    () => ({
      from: dateRange.start.toISOString(),
      to: dateRange.end.toISOString(),
    }),
    [dateRange.start, dateRange.end],
  );

  const {
    data: overviewData,
    isLoading: isLoadingOverview,
    error: overviewError,
  } = trpc.dashboard.getOverview.useQuery();
  const {
    data: financialData,
    isLoading: isLoadingFinancial,
    error: financialError,
  } = trpc.dashboard.getFinancialSnapshot.useQuery();
  const {
    data: postsTimeSeriesData,
    isLoading: isLoadingPostsSeries,
    error: postsSeriesError,
  } = trpc.analytics.getPostsTimeSeries.useQuery(dateParams);
  const {
    data: platformEngagementData,
    isLoading: isLoadingPlatformEngagement,
    error: platformEngagementError,
  } = trpc.analytics.getPlatformEngagement.useQuery(dateParams);
  const {
    data: contentStatusData,
    isLoading: isLoadingContentStatus,
    error: contentStatusError,
  } = trpc.analytics.getContentStatusBreakdown.useQuery(dateParams);

  const postsOverTime = useMemo(
    () =>
      postsTimeSeriesData?.map((item) => ({
        date: item.date,
        posts: item.posts,
      })) || [],
    [postsTimeSeriesData],
  );
  const platformEngagement = useMemo(() => {
    if (!platformEngagementData) return [];
    const colorMap: Record<string, string> = {
      twitter: chartColors.primary,
      linkedin: chartColors.secondary,
      instagram: chartColors.tertiary,
      tiktok: chartColors.quaternary,
      reddit: chartColors.quinary,
      email: chartColors.info,
    };
    return platformEngagementData.map((item) => ({
      platform: item.platform.charAt(0).toUpperCase() + item.platform.slice(1),
      impressions: item.total_posts * 1000,
      engagement: Math.floor(item.total_posts * 150),
      color: colorMap[item.platform.toLowerCase()] || chartColors.primary,
    }));
  }, [platformEngagementData]);
  const contentByStatus = useMemo(() => {
    if (!contentStatusData) return [];
    const colorMap: Record<string, string> = {
      published: chartColors.success,
      pending: chartColors.warning,
      draft: chartColors.info,
      failed: chartColors.error,
    };
    return contentStatusData.map((item) => ({
      status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
      count: item.count,
      color: colorMap[item.status.toLowerCase()] || chartColors.primary,
    }));
  }, [contentStatusData]);
  const totalPosts = contentByStatus.reduce((sum, item) => sum + item.count, 0);

  const kpis = [
    {
      label: "System Health",
      value: overviewData
        ? `${overviewData.apps_healthy}/${overviewData.total_apps}`
        : "-",
      trend: {
        value: overviewData?.avg_uptime_percent || 0,
        direction:
          (overviewData?.avg_uptime_percent || 0) >= 95
            ? ("up" as const)
            : ("down" as const),
      },
      icon: "H",
      glow:
        (overviewData?.apps_healthy || 0) === (overviewData?.total_apps || 0),
    },
    {
      label: "Total Posts",
      value: totalPosts.toLocaleString(),
      trend: { value: 0, direction: "neutral" as const },
      icon: "P",
    },
    {
      label: "Total Reach",
      value: `${(platformEngagement.reduce((sum, p) => sum + p.impressions, 0) / 1000).toFixed(1)}K`,
      trend: { value: 0, direction: "neutral" as const },
      icon: "R",
    },
    {
      label: "MRR",
      value: financialData
        ? `$${financialData.revenue.mrr.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
        : "-",
      trend: {
        value: financialData?.revenue.arr_growth_percent || 0,
        direction:
          (financialData?.revenue.arr_growth_percent || 0) >= 0
            ? ("up" as const)
            : ("down" as const),
      },
      icon: "$",
      glow: true,
    },
  ];

  return (
    <div className="min-h-screen bg-bg-primary p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">
              Analytics Overview
            </h1>
            <p className="mt-2 text-sm text-text-secondary">
              Track your content performance and system health metrics
            </p>
          </div>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
        </div>

        {(isLoadingOverview || isLoadingFinancial) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="rounded-lg border border-border-default bg-bg-tertiary p-6 h-32 animate-pulse"
              />
            ))}
          </div>
        )}
        {(overviewError || financialError) && (
          <div className="mb-8 rounded-lg border border-error-500 bg-error-50 p-6 text-center">
            <p className="text-sm text-error-500">
              Failed to load analytics data.
            </p>
          </div>
        )}
        {!isLoadingOverview &&
          !isLoadingFinancial &&
          !overviewError &&
          !financialError && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {kpis.map((kpi) => (
                <KPICard
                  key={kpi.label}
                  label={kpi.label}
                  value={kpi.value}
                  trend={kpi.trend}
                  icon={<span className="text-2xl font-bold">{kpi.icon}</span>}
                  glow={kpi.glow}
                />
              ))}
            </div>
          )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-lg border border-border-default bg-bg-tertiary p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Platform Activity
            </h2>
            {isLoadingPostsSeries ? (
              <ChartLoading height={256} />
            ) : postsSeriesError ? (
              <ChartError
                height={256}
                message="Failed to load posts time series data"
              />
            ) : postsOverTime.length > 0 ? (
              <ResponsiveChartContainer title="Platform activity" height={256}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={postsOverTime}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorPosts"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={chartColors.primary}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={chartColors.primary}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...defaultChartTheme.grid} />
                    <XAxis
                      dataKey="date"
                      {...defaultChartTheme.axis}
                      tick={{ ...defaultChartTheme.axisLabel }}
                    />
                    <YAxis
                      {...defaultChartTheme.axis}
                      tick={{ ...defaultChartTheme.axisLabel }}
                    />
                    <Tooltip
                      contentStyle={defaultChartTheme.tooltip.contentStyle}
                    />
                    <Legend
                      wrapperStyle={defaultChartTheme.legend.wrapperStyle}
                    />
                    <Area
                      type="monotone"
                      dataKey="posts"
                      name="Posts Published"
                      stroke={chartColors.primary}
                      fillOpacity={1}
                      fill="url(#colorPosts)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ResponsiveChartContainer>
            ) : (
              <ChartEmpty
                height={256}
                title="No post data yet"
                description="Post activity will appear here"
              />
            )}
          </div>

          <div className="rounded-lg border border-border-default bg-bg-tertiary p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Content by Status
            </h2>
            {isLoadingContentStatus ? (
              <ChartLoading height={256} />
            ) : contentStatusError ? (
              <ChartError
                height={256}
                message="Failed to load content status"
              />
            ) : contentByStatus.length > 0 ? (
              <ResponsiveChartContainer
                title="Content distribution"
                height={200}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={contentByStatus}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {contentByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={defaultChartTheme.tooltip.contentStyle}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </ResponsiveChartContainer>
            ) : (
              <ChartEmpty height={256} title="No content data" />
            )}
          </div>

          <div className="lg:col-span-3 rounded-lg border border-border-default bg-bg-tertiary p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Platform Comparison
            </h2>
            {isLoadingPlatformEngagement ? (
              <ChartLoading height={300} />
            ) : platformEngagementError ? (
              <ChartError height={300} message="Failed to load" />
            ) : platformEngagement.length > 0 ? (
              <ResponsiveChartContainer
                title="Platform comparison"
                height={300}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={platformEngagement}
                    margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid {...defaultChartTheme.grid} />
                    <XAxis
                      dataKey="platform"
                      {...defaultChartTheme.axis}
                      tick={{ ...defaultChartTheme.axisLabel }}
                    />
                    <YAxis
                      {...defaultChartTheme.axis}
                      tick={{ ...defaultChartTheme.axisLabel }}
                    />
                    <Tooltip
                      contentStyle={defaultChartTheme.tooltip.contentStyle}
                    />
                    <Legend
                      wrapperStyle={defaultChartTheme.legend.wrapperStyle}
                    />
                    <Bar
                      dataKey="impressions"
                      name="Impressions"
                      fill={chartColors.secondary}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="engagement"
                      name="Engagement"
                      fill={chartColors.tertiary}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ResponsiveChartContainer>
            ) : (
              <ChartEmpty height={300} title="No platform data" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
